import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Col, Row } from "reactstrap"
import { Waypoint } from "react-waypoint"
import Trip from "components/trips/Trip"
import Spinner from "components/common/Spinner"
import ZoomButtons from "components/map/ZoomButtons"
import CurrentLocationButton from "components/map/CurrentLocationButton"
import TripMapMarker from "components/trips/TripMapMarker"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import Loading from "modules/loader-watchers/Loading"
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api"
import { pick } from "lodash"
import { formatAddressComponent, getLocalStorageBrowserLocation } from "helpers/location"
import { isEmptyFields } from "helpers/form"
import { isLastPage } from "helpers/pagination"
import { useTitle } from "hooks/useTitle"
import useGeocoder from "modules/google/hooks/useGeocoder"
import useSearchFilter from "hooks/useSearchFilter"
import { Alert } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
// Redux
import { useDispatch, useSelector } from "react-redux"
import { cleanTrip, cleanTrips, getTrips } from "store/trips"
import { collectionAnySelector, collectionSelector, metadataSelector } from "store/selectors"
import { API_OPTIONS, DEFAULT_ZOOM, MAP_OPTIONS } from "constants/map"
import { TRIP_FILTERS, TRIP_FILTERS_HELP_ATTRIBUTES } from "constants/trip"
import { cleanedExperiences } from "store/experiences"
import EmptyState from "components/common/EmptyState"
import SortDropdownAntd from "components/trips/filters/TripsSort"
import styles from "./style.module.css"

export default function Trips() {
  useTitle("Search Trips")
  const { t } = useTranslation()
  const { isLoaded } = useJsApiLoader(API_OPTIONS)
  const dispatch = useDispatch()
  const geocoder = useGeocoder(null)
  const headerHeight = useSelector((state) => state.window.headerHeight)
  const trips = useSelector(collectionSelector("trips"))
  const hasTrips = useSelector(collectionAnySelector("trips"))
  const metadata = useSelector(metadataSelector("trips"))
  const [hoverItemId, setHoverItemId] = useState(null)
  const [activeItemId, setActiveItemId] = useState(null)
  const [sort, setSort] = useState({ price_sort: "", distance_sort: "" })
  const tripMouseOver = useRef(false)
  const [map, setMap] = useState(null)
  const [boundsLatLng, setBoundsLatLng] = useState(null)
  const [params, updateSearchFilters, searchUpdatedCallback] = useSearchFilter({
    accessAttributes: TRIP_FILTERS,
    exceptDependencies: TRIP_FILTERS_HELP_ATTRIBUTES,
    additionalDependencies: [boundsLatLng?.northeast_corner, boundsLatLng?.southwest_corner]
  })

  // selectors for all filters
  const priceRange = useSelector((state) => state.trips.priceRange)
  const language = useSelector((state) => state.trips.language)
  const skillLevel = useSelector((state) => state.trips.skillLevel)
  const experiences = useSelector((state) => state.trips.experiences)
  const other = useSelector((state) => state.trips.other)
  const tripsSort = useSelector((state) => state.trips.sort)
  const guests = useSelector((state) => state.trips.guests)
  const calendar = useSelector((state) => state.trips.calendar)
  const browserLocation = useMemo(() => getLocalStorageBrowserLocation(), [])
  const paramsLatLng = useMemo(() => (isEmptyFields(params, ["lat", "lng"]) ? null : pick(params, ["lat", "lng"])), [params])
  const [centerLatLng, setCenterLatLng] = useState(paramsLatLng || browserLocation)
  const zoom = map?.zoom || params.zoom || DEFAULT_ZOOM

  const updateSearchHandler = useCallback(
    (location) =>
      geocoder({ location }, (place) =>
        updateSearchFilters({ ...location, zoom, address: formatAddressComponent(place.address_components, ["locality", "political"]) })
      ),
    [zoom, geocoder, updateSearchFilters]
  )

  const setBoundsHandler = useCallback(
    (loadedMap) => {
      const bounds = (loadedMap || map)?.getBounds()
      if (!bounds) return
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()
      const northeast_corner = Object.keys(ne)
        .reduce((acc, key) => acc.concat(ne[key]()), [])
        .join(", ")
      const southwest_corner = Object.keys(sw)
        .reduce((acc, key) => acc.concat(sw[key]()), [])
        .join(", ")
      setBoundsLatLng({ northeast_corner, southwest_corner })
    },
    [map]
  )

  const onLoad = useCallback((map) => setMap(map), [])
  const onUnmount = useCallback(() => setMap(null), [])

  const onIdleHandler = useCallback(() => {
    const mapCenter = map?.center
    if (!mapCenter) return
    const lat = mapCenter.lat()
    const lng = mapCenter.lng()
    updateSearchHandler({ lat, lng })
    setBoundsHandler(map)
  }, [map, updateSearchHandler, setBoundsHandler])

  useEffect(() => {
    if (paramsLatLng) setCenterLatLng(paramsLatLng)
  }, [paramsLatLng?.lat, paramsLatLng?.lng]) //eslint-disable-line

  useEffect(() => {
    dispatch(cleanTrip())
    dispatch(cleanedExperiences())
    return () => {
      setMap(null)
      dispatch(cleanTrips())
    }
  }, []) //eslint-disable-line

  const fetchTrips = (nextPage = false) => {
    if (!boundsLatLng?.northeast_corner || !boundsLatLng?.southwest_corner) return

    const payload = {
      northeast_corner: boundsLatLng.northeast_corner,
      southwest_corner: boundsLatLng.southwest_corner,
      guest_count: guests.adults + guests.children === 0 ? "" : guests.adults + guests.children,
      from_date: calendar.from_date,
      to_date: calendar.to_date,
      priceRange: priceRange,
      skill_levels: skillLevel,
      alcohol_allowed: other.includes("Alcohol is Allowed"),
      accessibility_enabled: other.includes("Accessible"),
      children_allowed: other.includes("Child Friendly"),
      category_names: experiences,
      price_sort: tripsSort.price_sort,
      distance_sort: tripsSort.distance_sort,
      longitude: params.lng,
      latitude: params.lat,
      languages: language
    }

    if (!nextPage) window.scrollTo(0, 0)
    dispatch(getTrips(nextPage, payload))
  }

  searchUpdatedCallback(() => fetchTrips())

  // clear filters
  const handleClearFilters = () => {
    updateSearchFilters({})
  }

  useEffect(() => {
    fetchTrips()
    // eslint-disable-next-line
  }, [priceRange, language, skillLevel, experiences, other, tripsSort.price_sort, tripsSort.distance_sort])

  return (
    <Row className="flex-fill g-0 bg-light z-0">
      <Col
        xs={12}
        md={{ size: 7, order: 1 }}
        className="vstack sticky-top z-0 order-2"
        style={{ top: headerHeight, height: `calc(100vh - ${headerHeight}px)` }}
      >
        {isLoaded ? (
          <GoogleMap
            mapContainerClassName="h-100"
            options={{ ...MAP_OPTIONS, clickableIcons: false }}
            center={centerLatLng || paramsLatLng}
            zoom={zoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onIdle={onIdleHandler}
            onClick={() => !tripMouseOver.current && setActiveItemId(null)}
          >
            <div className="vstack position-absolute p-10 w-100 h-100 gap-10 pointer-events-none">
              <div className="pointer-events-auto hstack gap-2 ms-auto ">
                <CurrentLocationButton />
              </div>
              {centerLatLng ? (
                <>
                  {trips.map((trip) => (
                    <TripMapMarker trip={trip} key={trip.id} isActive={hoverItemId === trip.id} toggle={setActiveItemId} />
                  ))}
                  {activeItemId && (
                    <TripMapMarker
                      key={activeItemId}
                      trip={trips.find((trip) => trip.id === activeItemId)}
                      isActive
                      withPreview
                      toggle={setActiveItemId}
                      onMouseOver={() => (tripMouseOver.current = true)}
                      onMouseLeave={() => (tripMouseOver.current = false)}
                    />
                  )}
                </>
              ) : (
                <div className="hstack h-100 p-50">
                  <Spinner className="m-auto" />
                </div>
              )}
              <ZoomButtons />
            </div>
          </GoogleMap>
        ) : (
          <div className="hstack h-100 p-50">
            <Spinner className="m-auto" />
          </div>
        )}
      </Col>
      <Col xs={12} md={5} className="z-1 mt-50 mt-md-0">
        <FirstLoading name="trips" className="p-20 h-100">
          <Loading name="trips" className="vstack p-20 h-100 bg-white shadow gap-2" spinnerProps={{ position: "fixed" }}>
            {hasTrips ? (
              <>
                {/*  Header section */}
                <div className="mb-l h-auto">
                  <h4 className="fw-semibold">{t("filterTags.available_experiences")}</h4>
                  {metadata?.miles_away === true && (
                    <Alert
                      className={styles.alertInfo}
                      message={t("filterTags.no_matches_50_miles")}
                      type="info"
                      showIcon
                      closable
                      icon={<InfoCircleOutlined className={styles.infoIcon} />}
                    />
                  )}
                </div>

                <div className="hstack justify-between flex-wrap align-items-center gap-15 mb-xs">
                  <span>{t("filterTags.experiences_found", { count: metadata?.total_results })}</span>
                  <SortDropdownAntd value={sort} onChange={setSort} />
                </div>
                {/*Trip cards */}
                {trips.map((trip) => (
                  <Trip
                    trip={trip}
                    key={trip.id}
                    onMouseOver={() => setHoverItemId(trip.id)}
                    onMouseLeave={() => setHoverItemId(null)}
                    withRating
                  />
                ))}
                {!isLastPage(metadata) && <Waypoint onEnter={() => fetchTrips(true)} />}
              </>
            ) : (
              <EmptyState
                title={t("filterTags.no_experiences_found")}
                description={t("filterTags.no_matches_area")}
                onClear={handleClearFilters}
              />
            )}
          </Loading>
        </FirstLoading>
      </Col>
    </Row>
  )
}
